import { useTranslation } from "react-i18next";
import { shortReference } from "../../../shared/references/shortReferences";
import type { TFunction } from "i18next";
import Chip from "../../../ui/feedback/Chip";
import {
  FACT_SOURCES,
  routeKey,
  type ConnectorStatus,
  type FactSourceCounts,
  type ReportedApiRoute,
} from "../../../api/connectorStatus";
import { catalogueNote, joinedNotes, omittedNote } from "./catalogueNote";
import EmptyState from "../../../ui/feedback/EmptyState";

type Tone = "success" | "warning" | "danger";

interface RouteRow {
  readonly key: string;
  readonly name: string;
  readonly kind: string;
  readonly status: string;
  readonly tone: Tone;
  readonly models: number;
  readonly sources: FactSourceCounts;
  /** Shown under the row, such as why turns skip it. */
  readonly note: string | null;
}

/**
 * Every agent and API route a connector reported, as T3 Code lists its providers: whether
 * it works, what GTA last saw of an API route's key and credit, its models, and where the
 * facts about its capabilities come from (Q-93; capabilities.md CAP-1). Probing is
 * automatic, so there is nothing to start here.
 */
export default function ConnectorRoutes({ status }: { status: ConnectorStatus }) {
  const { t } = useTranslation();
  const rows: RouteRow[] = [
    ...status.agents.map((agent) => {
      const key = routeKey("agent", agent.agentId);
      return {
        key,
        name: agent.title ?? agent.agentId,
        kind: t("connectors.routes.agent"),
        status: t(`connectors.agentStatus.${agent.status}`),
        // Whether it runs is the server's answer (CP-11); the status names what the PC reported.
        tone: agent.unavailableBecause === null ? ("success" as const) : ("danger" as const),
        models: agent.models.length,
        sources: status.factSources[key] ?? {},
        note: joinedNotes(agent.unavailableBecause === null ? catalogueNote(t, agent.catalogue) : (agent.problem ?? catalogueNote(t, agent.catalogue)), omittedNote(t, agent.omittedFacts)),
      };
    }),
    ...status.apiRoutes.map((route) => apiRouteRow(t, route, status)),
  ];
  const usesApiRoutes = status.apiRoutes.some((route) => route.keyPresent);

  return (
    <section className="space-y-2" aria-label={t("connectors.routes.title")}>
      <h4 className="app-label text-sm">{t("connectors.routes.title")}</h4>
      {rows.length === 0 ? (
        <EmptyState>{t("connectors.routes.none")}</EmptyState>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="app-text-muted text-left">
              <th className="pr-2 font-normal">{t("connectors.routes.route")}</th>
              <th className="pr-2 font-normal">{t("connectors.routes.kind")}</th>
              <th className="pr-2 font-normal">{t("connectors.routes.status")}</th>
              <th className="pr-2 font-normal">{t("connectors.routes.models")}</th>
              <th className="font-normal">{t("connectors.routes.sources")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="align-top">
                <td className="pr-2 font-mono">{row.name}</td>
                <td className="pr-2">{row.kind}</td>
                <td className="pr-2">
                  <Chip tone={row.tone}>{row.status}</Chip>
                  {row.note === null ? null : <p className="app-text-muted mt-1">{row.note}</p>}
                </td>
                <td className="pr-2">{row.models}</td>
                <td>{sourcesText(t, row.sources)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {usesApiRoutes ? (
        <p className="app-text-muted text-xs">
          {status.gta === null
            ? t("connectors.routes.gtaNotStarted")
            : t("connectors.routes.gta", { version: status.gta.version ?? "?", revision: status.gta.revision === null ? "?" : shortReference("gitObject", status.gta.revision) })}
        </p>
      ) : null}
    </section>
  );
}

function apiRouteRow(t: TFunction, route: ReportedApiRoute, status: ConnectorStatus): RouteRow {
  const state = route.providerState;
  // The server's verdict is said first (CP-11); the provider's own observation only while it runs.
  const label = route.unavailableBecause !== null
    ? t(`threads.hosting.unavailable.${route.unavailableBecause}`)
    : state !== null
      ? t(`connectors.routes.providerStates.${state}`)
      : t(`connectors.agentStatus.${route.status}`);
  // Whether it runs is the server's answer (CP-11); the provider's own state only shades it.
  const tone: Tone = route.unavailableBecause !== null ? "danger" : state === "ok" || state === null ? "success" : "warning";
  const key = routeKey("api_key", route.routeId);
  return {
    key,
    name: route.routeId,
    kind: route.api === null ? route.baseUrlHost : `${t(`connectors.routes.apiKinds.${route.api}`)}, ${route.baseUrlHost}`,
    status: label,
    tone,
    models: route.models.length,
    sources: status.factSources[key] ?? {},
    note: joinedNotes(route.unavailableBecause === "blocked" ? t("connectors.routes.skipped") : route.keyPresent ? route.problem : null, omittedNote(t, route.omittedFacts)),
  };
}

function sourcesText(t: TFunction, sources: FactSourceCounts): string {
  const parts = FACT_SOURCES.flatMap((source) => {
    const count = sources[source] ?? 0;
    return count === 0 ? [] : [`${t(`threads.hosting.facts.sources.${source}`)} ${String(count)}`];
  });
  return parts.length === 0 ? t("connectors.routes.noFacts") : parts.join(", ");
}
